import { isAxiosError } from 'axios';

export function isErrorLike(e: unknown): e is Error {
  return e instanceof Error || (typeof e === 'object' && e !== null && 'name' in e);
}

export function coerceErrorToString(e: unknown) {
  if (isErrorLike(e)) {
    return e.message || e.name;
  }
  if (typeof e === 'string') {
    return e;
  }
  if (typeof e === 'object' && e !== null && 'constructor' in e && 'name' in e.constructor) {
    return e.constructor.name;
  }
  return String(e);
}

const MISSING_PERMISSIONS = 'MissingPermissions';
const RESOURCE_NOT_FOUND = 'ResourceNotFound';

export interface NetworkErrorOptions<T = { value: string }> {
  availableOptions?: T[];
  customMessage?: string;
}

/**
 * Checks if an Axios error matches common HTTP status codes and returns a custom named error to prevent specific error logging
 * Handles: 400 (Bad Request), 401 (Unauthorized), 403 (Forbidden), 404 (Not Found), 500 (Internal Server Error)
 * For 403 errors, can enhance with available options/packages information
 * @param error - The error to check
 * @param options - Optional configuration (customMessage, availableOptions)
 * @returns Custom Error with specific name if status matches, or the enhanced original error for 403 with availableOptions, undefined otherwise
 */
export const handleNetworkError = <T = { value: string }>(
  error: unknown,
  options?: NetworkErrorOptions<T>
): Error | undefined => {
  if (!isAxiosError(error)) {
    return undefined;
  }

  const status = error.response?.status;
  if (!status) {
    return undefined;
  }

  const { customMessage, availableOptions } = options ?? {};

  let defaultErrorName: string;
  let defaultMessage: string;

  switch (status) {
    case 403:
      defaultErrorName = MISSING_PERMISSIONS;
      const failedRoute = error.config?.url;

      defaultMessage = error.message;
      // Only include available packages info for /ui-config/advisor route
      if (failedRoute?.includes('/ui-config/advisor') && availableOptions && availableOptions.length > 0) {
        const availableValues = availableOptions.map((option) => (option as { value: string }).value).join(', ');
        defaultMessage = `Available packages: ${availableValues}`;
      }
      console.error('[APIFailureDebug] Permission denied for API:', {
        status: error.response?.status,
        url: error.config?.url,
        params: error.config?.params,
        message: defaultMessage,
        timestamp: new Date().toISOString(),
      });

      break;
    case 404:
      defaultErrorName = RESOURCE_NOT_FOUND;
      defaultMessage = error.message;
      console.error('[APIFailureDebug] Requested resource not found:', {
        status: error.response?.status,
        url: error.config?.url,
        params: error.config?.params,
        message: defaultMessage,
        timestamp: new Date().toISOString(),
      });
      break;
    default:
      // Status code not handled by this function
      return undefined;
  }

  const message = customMessage ?? defaultMessage;

  const customError = new Error(message);
  customError.name = defaultErrorName;
  customError.stack = error.stack; // Preserve original stack trace
  customError.cause = error.cause; // Preserve original cause if available
  return customError;
};
